import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";

import { MessageResponseDto } from "@api/api";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import {
  useQyCreateBulkPayment,
  useQyCreatePayment,
} from "@factory/query/payment.query";
import { PaymentFormRule, PaymentFormSchema } from "@factory/form/form.rule";
import { paymentFormDefault } from "@factory/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useQueryClient } from "react-query";
import { QueryKeyBilling } from "@factory/model/query-model-billing";
import { useUserIdentityContext } from "@core/services/user-identity.context";

export function useNewPayment(isBulk?: boolean) {
  // LOCAL DECLARATION
  const navigate = useNavigate();
  const { mapCreatePayment, mapCreateBulkPayment } = useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();
  const { contractId } = useParams();
  const [search] = useSearchParams();
  const billingId = search.get("billingId");
  const queryClient = useQueryClient();
  const { getUserRole } = useUserIdentityContext();
  const { slug } = getUserRole();

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;
    const contract: string = data.contract;

    queryClient.invalidateQueries({
      queryKey: [
        QueryKeyBilling,
        "",
        9999,
        0,
        undefined,
        { contract: contractId || "" },
      ],
      exact: false,
    });

    showSuccess(`New Payment is created`);
    if (contractId) {
      navigate(`/${slug}/contract/${contractId}/billing`);
    } else {
      navigate(-1);
    }
  };
  const { mutate: addPayment, isLoading: isCreating } =
    useQyCreatePayment(handleApiSuccess);

  // API Pay Bulk Contract
  const handleUpdateApiBulkPaySuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    queryClient.invalidateQueries({
      queryKey: [
        QueryKeyBilling,
        "",
        9999,
        0,
        undefined,
        { contract: contractId || "" },
      ],
      exact: false,
    });

    showSuccess(`New Payment is created`);
    if (contractId) {
      navigate(`/${slug}/contract/${contractId}/billing`);
    } else {
      navigate(-1);
    }
  };
  const { mutate: bulkPay } = useQyCreateBulkPayment(
    handleUpdateApiBulkPaySuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<PaymentFormSchema>({
    defaultValues: paymentFormDefault,
    resolver: zodResolver(PaymentFormRule),
  });
  const { handleSubmit } = formMethod;

  const handleValidate = (form: PaymentFormSchema) => {
    if (isBulk) {
      const updatedForm = contractId
        ? ({
            ...form,
            contract: contractId,
          } as PaymentFormSchema)
        : form;

      const formData = mapCreateBulkPayment(updatedForm);
      bulkPay(formData);
      return;
    }

    const updatedForm = billingId
      ? ({
          ...form,
          billing: billingId,
        } as PaymentFormSchema)
      : form;

    const formData = mapCreatePayment(updatedForm);
    addPayment(formData);
  };
  const handleValidateError = (err: FieldErrors<PaymentFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return {
    isCreating,
    formMethod,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  };
}
